import {
  AccountTree,
  Delete,
  Done,
  Edit,
  LaptopRounded,
  LocationCity,
  Login,
  Newspaper,
  Person,
  Settings,
  Shield,
  Storage,
  Work,
} from "@mui/icons-material";

import React from "react";
import { Action, EntityType } from "./type";
import { CustomDateFilter } from "@common/components/DatePopover";
import { Room } from "@assets/icons";
import { AuditFilters } from "@services/audit/types";

export const defaultEntityTypeFilter: EntityType[] = [
  {
    label: "System",
    type: "SYSTEM",
    icon: <Storage />,
  },
  {
    label: "Account",
    type: "ACCOUNT",
    icon: <Work />,
  },
  {
    label: "User",
    type: "USER",
    icon: <Person />,
  },
  {
    label: "Location",
    type: "LOCATION",
    icon: <LocationCity />,
  },
  {
    label: "Room",
    type: "ROOM",
    icon: <Room />,
  },
  {
    label: "Device",
    type: "DEVICE",
    icon: <LaptopRounded />,
  },
  {
    label: "Catalog",
    type: "DEVICE_CATALOG_METADATA",
    icon: <Newspaper />,
  },
  {
    label: "Property",
    type: "PROPERTIES",
    icon: <Settings />,
  },
];

export const defaultActionFilter: Action[] = [
  {
    label: "Created",
    type: "Created",
    color: "default",
    icon: <Done />,
  },
  {
    label: "Deleted",
    type: "Deleted",
    color: "default",
    icon: <Delete />,
  },
  {
    label: "Modified",
    type: "Modified",
    color: "default",
    icon: <Edit />,
  },
  {
    label: "Login",
    type: "Login",
    color: "default",
    icon: <Login />,
  },
  {
    label: "Protected",
    type: "Protected",
    color: "default",
    icon: <Shield />,
  },
  {
    label: "Workflow",
    type: "Workflow",
    color: "default",
    icon: <AccountTree />,
  },
  {
    label: "Control",
    type: "Control",
    color: "default",
    icon: <Settings />,
  },
];

export enum EntityTypeList {
  PROPERTIES = "PROPERTIES",
  SYSTEM = "SYSTEM",
  ACCOUNT = "ACCOUNT",
  USER = "USER",
  LOCATION = "LOCATION",
  ROOM = "ROOM",
  DEVICE = "DEVICE",
  DEVICE_CATALOG_METADATA = "DEVICE_CATALOG_METADATA",
}

const assetLevelActionTypes: Array<Action["type"]> = [
  "Created",
  "Deleted",
  "Modified",
  "Protected",
];

const isAssetLevelEntity = (type: EntityTypeList) =>
  [
    EntityTypeList.ACCOUNT,
    EntityTypeList.LOCATION,
    EntityTypeList.ROOM,
    EntityTypeList.PROPERTIES,
  ].includes(type);

export const getAvailableActions = (
  entityTypes: EntityTypeList[] | EntityTypeList
) => {
  const types = Array.isArray(entityTypes) ? entityTypes : [entityTypes];

  if (types.some(isAssetLevelEntity)) {
    return defaultActionFilter.filter((item) =>
      assetLevelActionTypes.includes(item.type)
    );
  }

  if (types.includes(EntityTypeList.DEVICE)) {
    return defaultActionFilter.filter((item) => item.type !== "Login");
  }

  return defaultActionFilter;
};

export const getEntityTypeArray = (
  slFilters: AuditFilters,
  entityType?: EntityTypeList | ""
): EntityTypeList[] => {
  const entityTypeArray = entityType ? [entityType] : slFilters.entityType;
  if (entityType && entityType === EntityTypeList.PROPERTIES)
    entityTypeArray?.push(EntityTypeList.DEVICE_CATALOG_METADATA);
  return entityTypeArray as EntityTypeList[];
};

export const globalMinimumDate = 1577833200000;
export const basicTimeFilter: CustomDateFilter[] = [
  {
    label: "Today",
    getDates: (): { startDate: number; endDate: number } => ({
      startDate: new Date(
        new Date().getFullYear(),
        new Date().getMonth(),
        new Date().getDate()
      ).getTime(),
      endDate: new Date().getTime(),
    }),
  },
  {
    label: "This week",
    getDates: (): { startDate: number; endDate: number } => ({
      startDate: new Date(
        new Date().getFullYear(),
        new Date().getMonth(),
        new Date().getDate() - 7
      ).getTime(),
      endDate: new Date().getTime(),
    }),
  },
  {
    label: "This month",
    getDates: (): { startDate: number; endDate: number } => ({
      startDate: new Date(
        new Date().getFullYear(),
        new Date().getMonth() - 1,
        new Date().getDate()
      ).getTime(),
      endDate: new Date().getTime(),
    }),
  },
];
export const allTimeFilter: CustomDateFilter = {
  label: "All",
  getDates: (): { startDate: number; endDate: number } => ({
    startDate: globalMinimumDate,
    endDate: new Date().getTime(),
  }),
};
export const customTimeFilter: CustomDateFilter = {
  label: "Custom",
  getDates: (): { startDate: number; endDate: number } => ({
    startDate: globalMinimumDate,
    endDate: new Date().getTime(),
  }),
};
export const threeMonthsTimeFilter: CustomDateFilter = {
  label: "Custom",
  getDates: (): { startDate: number; endDate: number } => ({
    startDate: new Date(
      new Date().getFullYear(),
      new Date().getMonth() - 3,
      new Date().getDate()
    ).getTime(),
    endDate: new Date().getTime(),
  }),
};
